import React, { useState, useEffect } from 'react';
import { Palette, Monitor, Sun, Moon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import CustomColorPicker from './CustomColorPicker';
import { themePresets, defaultTheme } from '@/config/themePresets';
import { applyGlobalThemeColors } from '@/utils/colorUtils';

/**
 * Theme Settings Component
 * 
 * A dropdown component for theme and display settings that matches AutoERP styling
 * 
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 */
const ThemeSettings = ({ className = "" }) => {
  const [theme, setTheme] = useState('light');
  const [currentThemeName, setCurrentThemeName] = useState('Emerald Glow');
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  // Load saved preferences on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const savedCustomTheme = localStorage.getItem('customTheme');
    
    setTheme(savedTheme);
    applyTheme(savedTheme);
    
    // Apply custom theme colors if they exist
    if (savedCustomTheme) {
      try {
        const parsed = JSON.parse(savedCustomTheme);
        const preset = themePresets.find(p => p.id === parsed.selectedPreset);
        setCurrentThemeName(preset ? preset.name : 'Custom Theme');
        
        // Apply the custom colors
        const currentMode = savedTheme === 'dark' || (savedTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
        applyCustomThemeColors(parsed[currentMode]);
      } catch (error) {
        console.error('Failed to parse saved theme:', error);
        setCurrentThemeName(defaultTheme.name);
      }
    } else {
      setCurrentThemeName(defaultTheme.name);
    }

    // Listen for system theme changes when using system theme
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = () => {
      if (savedTheme === 'system') {
        applyTheme('system');
        // Reapply custom colors for the new mode
        if (savedCustomTheme) {
          try {
            const parsed = JSON.parse(savedCustomTheme);
            const newMode = mediaQuery.matches ? 'dark' : 'light';
            applyCustomThemeColors(parsed[newMode]);
          } catch (error) {
            console.error('Failed to apply custom theme on system change:', error);
          }
        }
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);

  /**
   * Applies custom theme colors (same logic as in CustomColorPicker)
   */
  const applyCustomThemeColors = (colors) => {
    if (!colors) return;
    
    try {
      // Use the global theme application utility
      applyGlobalThemeColors(colors);
    } catch (error) {
      console.error('Failed to apply custom theme colors:', error);
    }
  };

  const applyTheme = (selectedTheme) => {
    const root = document.documentElement;
    if (selectedTheme === 'dark') {
      root.classList.add('dark');
    } else if (selectedTheme === 'light') {
      root.classList.remove('dark');
    } else if (selectedTheme === 'system') {
      // Use system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (systemPrefersDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
    localStorage.setItem('theme', selectedTheme);
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    
    // Reapply custom colors for the new theme mode
    const savedCustomTheme = localStorage.getItem('customTheme');
    if (savedCustomTheme) {
      try {
        const parsed = JSON.parse(savedCustomTheme);
        // Determine the new mode after theme change
        let newMode = 'light';
        if (newTheme === 'dark') {
          newMode = 'dark';
        } else if (newTheme === 'system') {
          newMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        
        // Apply custom colors for the new mode
        setTimeout(() => {
          applyCustomThemeColors(parsed[newMode]);
        }, 100); // Small delay to ensure theme class is applied first
      } catch (error) {
        console.error('Failed to reapply custom theme on mode change:', error);
      }
    }
  };

  const getModeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="h-4 w-4" />;
      case 'dark':
        return <Moon className="h-4 w-4" />;
      case 'system':
        return <Monitor className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={className}
            aria-label="Toggle theme"
          >
            {getModeIcon()}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Theme Settings</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Theme Mode Selection */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Theme Mode
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => handleThemeChange('light')}
            className="cursor-pointer"
          >
            <Sun className="mr-2 h-4 w-4" />
            <span>Light</span>
            {theme === 'light' && (
              <span className="ml-auto text-primary">✓</span>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleThemeChange('dark')}
            className="cursor-pointer"
          >
            <Moon className="mr-2 h-4 w-4" />
            <span>Dark</span>
            {theme === 'dark' && (
              <span className="ml-auto text-primary">✓</span>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleThemeChange('system')}
            className="cursor-pointer"
          >
            <Monitor className="mr-2 h-4 w-4" />
            <span>System</span>
            {theme === 'system' && (
              <span className="ml-auto text-primary">✓</span>
            )}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Current Theme Display */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Current Theme
          </DropdownMenuLabel>
          <div className="px-2 py-1.5 text-sm">
            {currentThemeName}
          </div>

          <DropdownMenuSeparator />

          {/* Customize Colors Button */}
          <DropdownMenuItem
            onClick={() => setIsColorPickerOpen(true)}
            className="cursor-pointer"
          >
            <Palette className="mr-2 h-4 w-4" />
            <span>Customize Colors</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* CustomColorPicker Dialog */}
      <CustomColorPicker
        open={isColorPickerOpen}
        onOpenChange={setIsColorPickerOpen}
      />
    </>
  );
};

export default ThemeSettings;